// AGC: PlotTimelineOverlay - Full-screen timeline view for a plot
// AGC: Shows chronological feed of milestones, tasks, and notes with vertical timeline

import React, { useState, useMemo } from 'react';
import { X, ZoomIn, ZoomOut, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../hooks/useTranslation';
import { usePlotTimeline } from '../../hooks/usePlotTimeline';
import { useLotes } from '../../context';
import { NoteDetailView } from '../notes/NoteDetailView';
import { getNoteById } from '../../services/lotNotesService';
import type { PlotTimelineEvent, TimelineSection } from '../../types/plotTimeline';
import type { LotNote } from '../../types/lotNotes';
import type { Screen } from '../../types';

interface PlotTimelineOverlayProps {
  plotId: string;
  plotName: string;
  onClose: () => void;
  onNavigate: (screen: Screen, data?: Record<string, unknown>) => void;
}

export const PlotTimelineOverlay: React.FC<PlotTimelineOverlayProps> = ({
  plotId,
  plotName,
  onClose,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const { getPlotById } = useLotes();
  const [zoomLevel, setZoomLevel] = useState(1);
  // AGC: Note detail modal state
  const [selectedNote, setSelectedNote] = useState<LotNote | null>(null);

  // AGC: Get plot data
  const plot = useMemo(() => getPlotById(plotId), [plotId, getPlotById]);

  // AGC: Extract dates from plot
  const plantingDate = useMemo(() => {
    if (!plot?.planning?.plantingDate) return new Date();
    return new Date(plot.planning.plantingDate);
  }, [plot]);

  const harvestDate = useMemo(() => {
    if (!plot?.planning?.estimatedHarvestDate) {
      // AGC: If no harvest date, estimate 120 days from planting
      const estimated = new Date(plantingDate);
      estimated.setDate(estimated.getDate() + 120);
      return estimated;
    }
    return new Date(plot.planning.estimatedHarvestDate);
  }, [plot, plantingDate]);

  const cropName = useMemo(() => {
    return plot?.cropName || plot?.cropType || 'Crop';
  }, [plot]);

  // AGC: Handle task click — must use `selectedTaskId` key so TaskDetailScreen
  // AGC: picks it up from location.state (all other callers use this key).
  const handleTaskClick = (taskId: string) => {
    onNavigate('task-detail', { selectedTaskId: taskId });
  };

  const { timelineData, isLoading } = usePlotTimeline({
    plotId,
    plotName,
    cropName,
    plantingDate,
    harvestDate,
  });

  // AGC: Zoom controls
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.2, 0.5));

  // AGC: Card scale based on zoom
  const cardScale = useMemo(() => {
    return 0.7 + zoomLevel * 0.3; // Scale from 0.7 to 1.3
  }, [zoomLevel]);

  // AGC: Calculate today's position
  const todayPosition = useMemo(() => {
    if (!timelineData) return 0;
    const { cycleStartDate, cycleEndDate } = timelineData.config;
    const now = new Date();
    const t = (now.getTime() - cycleStartDate.getTime()) / (cycleEndDate.getTime() - cycleStartDate.getTime());
    return Math.floor(t * 2000 * zoomLevel);
  }, [timelineData, zoomLevel]);

  // AGC: Get stage icon
  const getStageIcon = (stageCode: string) => {
    const icons: Record<string, string> = {
      VE: '🌱',
      V1: '🌿',
      V3: '🌿',
      V6: '🌿',
      R1: '🌺',
      R3: '🌸',
      R5: '🫘',
      R8: '🌾',
    };
    return <span className="text-lg">{icons[stageCode] || '📍'}</span>;
  };

  // AGC: Get month labels
  const monthLabels = useMemo(() => {
    if (!timelineData) return [];
    const { cycleStartDate, cycleEndDate } = timelineData.config;
    const labels: { month: string; position: number }[] = [];
    
    const current = new Date(cycleStartDate);
    while (current <= cycleEndDate) {
      const t = (current.getTime() - cycleStartDate.getTime()) / (cycleEndDate.getTime() - cycleStartDate.getTime());
      const position = Math.floor(t * 2000 * zoomLevel);
      
      const monthNames = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
      labels.push({
        month: monthNames[current.getMonth()],
        position,
      });
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return labels;
  }, [timelineData, zoomLevel]);

  // AGC: Get week markers
  const weekMarkers = useMemo(() => {
    if (!timelineData) return [];
    const { cycleStartDate, cycleEndDate, totalDays } = timelineData.config;
    const markers: number[] = [];
    
    for (let day = 7; day < totalDays; day += 7) {
      const t = day / totalDays;
      markers.push(Math.floor(t * 2000 * zoomLevel));
    }
    
    return markers;
  }, [timelineData, zoomLevel]);

  // AGC: Get stage background color
  const getStageColor = (stageCode: string): string => {
    // AGC: Color mapping for phenological stages with stronger opacity
    const colors: Record<string, string> = {
      'VE': 'rgba(220, 252, 231, 0.5)', // Light green - Emergence
      'V1': 'rgba(187, 247, 208, 0.5)', // Green - Vegetative 1
      'V2': 'rgba(134, 239, 172, 0.5)', // Green - Vegetative 2
      'V3': 'rgba(134, 239, 172, 0.5)', // Green - Vegetative 3
      'V4': 'rgba(74, 222, 128, 0.5)',  // Green - Vegetative 4
      'V5': 'rgba(34, 197, 94, 0.5)',   // Green - Vegetative 5
      'V6': 'rgba(22, 163, 74, 0.5)',   // Dark green - Vegetative 6
      'R1': 'rgba(254, 249, 195, 0.5)', // Yellow - Reproductive 1
      'R2': 'rgba(254, 240, 138, 0.5)', // Yellow - Reproductive 2
      'R3': 'rgba(253, 224, 71, 0.5)',  // Yellow - Reproductive 3
      'R4': 'rgba(250, 204, 21, 0.5)',  // Orange-yellow - Reproductive 4
      'R5': 'rgba(251, 191, 36, 0.5)',  // Orange - Reproductive 5
      'R6': 'rgba(245, 158, 11, 0.5)',  // Orange - Reproductive 6
      'R7': 'rgba(217, 119, 6, 0.5)',   // Dark orange - Reproductive 7
      'R8': 'rgba(194, 65, 12, 0.5)',   // Red-orange - Reproductive 8 (Harvest)
    };
    return colors[stageCode] || 'rgba(229, 231, 235, 0.3)'; // Default gray
  };

  // AGC: Handle event click
  const handleEventClick = async (event: PlotTimelineEvent) => {
    if (event.type === 'task') {
      handleTaskClick((event as any).taskId);
    } else if (event.type === 'note') {
      // AGC: Fetch full note data and open detail modal
      const noteId = (event as any).noteId;
      if (noteId) {
        try {
          const note = await getNoteById(noteId);
          if (note) setSelectedNote(note);
        } catch (err) {
          console.error('AGC: Failed to load note for detail view', err);
        }
      }
    }
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-background flex items-center justify-center"
      >
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </motion.div>
    );
  }

  if (!timelineData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-background flex items-center justify-center"
      >
        <div className="text-center">
          <p className="text-red-500">{t('plotTimeline.errorLoading')}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-accent-500 text-white rounded-lg">
            {t('common.close')}
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-background overflow-hidden"
    >
      {/* AGC: Header — safe-area-inset-top so it sits below Android status bar */}
      <div
        className="sticky top-0 z-10 bg-background border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">{plotName}</h1>
            <p className="text-sm text-gray-500">{cropName}</p>
          </div>
        </div>
        <Calendar className="w-5 h-5 text-gray-400" />
      </div>

      {/* AGC: Timeline Content */}
      <div className="relative h-[calc(100vh-64px)] overflow-y-auto overflow-x-hidden">
        <div className="relative pt-8 pb-8" style={{ height: `${2000 * zoomLevel + 64}px`, minHeight: '100%' }}>
          
          {/* AGC: Stage background colors - continuous sections */}
          {timelineData.sections.map((section, idx) => {
            // AGC: Calculate continuous section positions
            let sectionStartPos = 0;
            let sectionHeight = 0;
            
            if (timelineData.sections.length > 0) {
              // AGC: Calculate proportional heights based on date ranges
              const totalTimelineDuration = timelineData.config.cycleEndDate.getTime() - timelineData.config.cycleStartDate.getTime();
              
              // AGC: Get section date range
              const sectionStart = section.events.length > 0 
                ? Math.min(...section.events.map(e => e.eventDate.getTime()))
                : timelineData.config.cycleStartDate.getTime();
              
              const nextSection = timelineData.sections[idx + 1];
              const sectionEnd = nextSection && nextSection.events.length > 0
                ? Math.min(...nextSection.events.map(e => e.eventDate.getTime()))
                : timelineData.config.cycleEndDate.getTime();
              
              // AGC: Calculate position and height
              const startRatio = (sectionStart - timelineData.config.cycleStartDate.getTime()) / totalTimelineDuration;
              const endRatio = (sectionEnd - timelineData.config.cycleStartDate.getTime()) / totalTimelineDuration;
              
              sectionStartPos = (startRatio * 2000 * zoomLevel) + 32; // Add 32px padding
              sectionHeight = ((endRatio - startRatio) * 2000 * zoomLevel);
            }
            
            return (
              <div
                key={`bg-${section.id}`}
                className="absolute left-0 right-0"
                style={{
                  top: `${sectionStartPos}px`,
                  height: `${sectionHeight}px`,
                  backgroundColor: getStageColor(section.stageCode),
                  zIndex: 0,
                }}
              />
            );
          })}
          
          {/* AGC: Month labels on far left */}
          <div className="absolute left-0 top-8 w-7 text-[11px] font-bold text-gray-700 dark:text-gray-300 z-10">
            {monthLabels.map((label, idx) => (
              <div
                key={idx}
                className="absolute w-full text-center"
                style={{ top: `${label.position}px`, transform: 'translateY(-50%)' }}
              >
                {label.month}
              </div>
            ))}
          </div>

          {/* AGC: Vertical phenological stage labels */}
          <div className="absolute left-8 top-8 w-12 text-center text-gray-700 dark:text-gray-300 text-[11px] font-semibold z-10">
            {timelineData.sections.map((section) => {
              // AGC: Calculate label position (center of section)
              const firstEventPos = Math.min(...section.events.map(e => (e.pixelPosition || 0) * zoomLevel)) + 32;
              const lastEventPos = Math.max(...section.events.map(e => ((e.pixelPosition || 0) + (e.offsetY || 0)) * zoomLevel)) + 32;
              const centerPos = (firstEventPos + lastEventPos) / 2;
              
              return (
                <div
                  key={section.id}
                  className="absolute w-full flex items-center justify-center"
                  style={{ 
                    top: `${centerPos}px`,
                    transform: 'translateY(-50%)',
                  }}
                >
                  <div className={`flex flex-col items-center gap-0.5 ${
                    section.isCurrentStage ? 'text-accent-600 dark:text-accent-400' : ''
                  }`}>
                    <span className="text-lg">{getStageIcon(section.stageCode)}</span>
                    <span className="whitespace-nowrap text-[10px] font-bold">
                      {section.stageCode}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AGC: Vertical blue timeline line */}
          <div 
            className="absolute left-20 w-0.5 bg-blue-500 z-5"
            style={{ top: '32px', height: `${2000 * zoomLevel}px` }}
          >
            {/* AGC: Week markers */}
            {weekMarkers.map((position, idx) => (
              <div
                key={idx}
                className="absolute w-2 h-px bg-blue-400 -left-[3px]"
                style={{ top: `${position}px` }}
              />
            ))}
          </div>

          {/* AGC: Today marker (red line on left) */}
          <div
            className="absolute left-0 w-20 h-0.5 bg-red-500 z-20"
            style={{ top: `${todayPosition + 32}px` }}
          >
            <span className="absolute left-0 -top-3 text-[9px] font-bold text-red-500 whitespace-nowrap">
              {t('plotTimeline.today')}
            </span>
          </div>

          {/* AGC: Event cards with connector wires */}
          {timelineData.events.map((event, idx) => {
            const topPosition = (event.pixelPosition! + event.offsetY!) * zoomLevel + 32; // Add padding
            console.log(`🎨 AGC: Rendering event ${idx + 1}/${timelineData.events.length}: ${event.title} at top=${topPosition}px (pixel=${event.pixelPosition}, offset=${event.offsetY}, zoom=${zoomLevel})`);
            
            return (
            <React.Fragment key={event.id}>
              {/* AGC: Connector wire */}
              {event.offsetY !== 0 && (
                <svg
                  className="absolute left-20 w-16 pointer-events-none z-15"
                  style={{
                    top: `${event.pixelPosition! * zoomLevel + 32}px`,
                    height: `${event.offsetY * zoomLevel}px`,
                  }}
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="64"
                    y2={event.offsetY * zoomLevel}
                    stroke={event.type === 'task' ? 'var(--color-accent)' : 'var(--color-purple)'}
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                  />
                </svg>
              )}

              {/* AGC: Event card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0 }}
                className="absolute left-28 cursor-pointer"
                style={{
                  top: `${topPosition}px`,
                  transform: `scale(${cardScale})`,
                  transformOrigin: 'top left',
                  zIndex: 20 + (event.zIndex || 0),
                  maxWidth: 'calc(100vw - 120px)',
                }}
                onClick={() => handleEventClick(event)}
              >
                <div
                  className={`p-3 rounded-lg border-2 shadow-md transition-all hover:shadow-lg ${
                    event.type === 'milestone'
                      ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300'
                      : event.type === 'task'
                      ? event.isCompleted
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300'
                        : 'bg-white dark:bg-gray-800 border-accent-300'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300'
                  }`}
                  style={{ minWidth: '200px', maxWidth: '400px' }}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{event.icon}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{event.title}</h3>
                      {event.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {event.eventDate.toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </React.Fragment>
          )})}
        </div>
      </div>

      {/* AGC: Zoom controls (fixed on right) */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 border border-gray-200 dark:border-gray-700">
        <button
          onClick={handleZoomIn}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          disabled={zoomLevel >= 2}
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <div className="h-px bg-gray-200 dark:bg-gray-700" />
        <button
          onClick={handleZoomOut}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          disabled={zoomLevel <= 0.5}
        >
          <ZoomOut className="w-5 h-5" />
        </button>
      </div>

      {/* AGC: Note Detail Modal — opens when a note event is clicked */}
      <NoteDetailView
        note={selectedNote}
        isOpen={!!selectedNote}
        onClose={() => setSelectedNote(null)}
      />
    </motion.div>
  );
};
